import React, { Component, createRef } from "react";
import ArrowLeftSVG from "../../../Assets/svg/ArrowLeftSVG.svg";
import PencilSimpleLineSVG from "../../../Assets/svg/PencilSimpleLine.svg";
import FloppyDiskBackSVG from "../../../Assets/svg/FloppyDiskBack.svg";
import { withRouterHooks } from "../../../withRouters/withRoutersHook";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import ReactMarkdown from "react-markdown";
import MDEditor from "@uiw/react-md-editor";
import "@uiw/react-md-editor/markdown-editor.css";
import "@uiw/react-markdown-preview/markdown.css";
import * as pdfjsLib from "pdfjs-dist/build/pdf";
import {
  renderFileAction,
  FetchIndividualFileAction,
  editAnalysisOutputAction,
} from "../../../Actions/Analysis";

pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

class PDFExtractorComparator extends Component {
  static propTypes = {
    pdfAnalysisOutput: PropTypes.any,
    renderedPDF: PropTypes.string,
    contentType: PropTypes.string,
    PDFisRendered: PropTypes.bool,
    renderFileAction: PropTypes.func.isRequired,
    FetchIndividualFileAction: PropTypes.func.isRequired,
    editAnalysisOutputAction: PropTypes.func.isRequired,
    AddedFileID: PropTypes.string,
    navigate: PropTypes.func.isRequired,
  };

  canvasRef = createRef();

  state = {
    isEditing: false,
    editedContent: "",
    isSaving: false,
    error: null,
    debugData: null,
    // PDF page rendering
    pdfDoc: null,
    currentPage: 1,
    totalPages: 0,
    pageImages: {},
    loadingPage: false,
  };

  fetchID = () => {
    const path = this.props.location.pathname;
    const segments = path.split("/");
    const id = segments[segments.length - 2];
    return id;
  };

  componentDidMount() {
    const fileId = this.fetchID();

    this.props
      .FetchIndividualFileAction(fileId)
      .then((data) => {
        this.setState({ debugData: data });
      })
      .catch(() => {});

    // Load PDF as pages via pdfjs
    this.props.renderFileAction(fileId);
  }

  componentDidUpdate(prevProps) {
    const { pdfAnalysisOutput, renderedPDF, contentType } = this.props;

    if (prevProps.pdfAnalysisOutput !== pdfAnalysisOutput && pdfAnalysisOutput) {
      const analysisOutput = pdfAnalysisOutput.analysis_output;
      if (analysisOutput) {
        this.setState({ editedContent: this.processDataToText(analysisOutput) });
      }
    }

    // When the PDF blob URL is ready, load it with pdfjs
    if (renderedPDF && renderedPDF !== prevProps.renderedPDF && contentType === "application/pdf") {
      this.loadPdfDocument(renderedPDF);
    }
  }

  loadPdfDocument = async (blobUrl) => {
    try {
      const response = await fetch(blobUrl);
      const arrayBuffer = await response.arrayBuffer();
      const pdfDoc = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      this.setState({ pdfDoc, totalPages: pdfDoc.numPages, currentPage: 1 }, () => {
        this.renderPage(1);
      });
    } catch (err) {
      console.error("Failed to load PDF:", err);
    }
  };

  renderPage = async (pageNum) => {
    const { pdfDoc, pageImages } = this.state;
    if (!pdfDoc) return;

    // Use cached image if available
    if (pageImages[pageNum]) {
      this.setState({ currentPage: pageNum, loadingPage: false });
      return;
    }

    this.setState({ loadingPage: true, currentPage: pageNum });
    try {
      const page = await pdfDoc.getPage(pageNum);
      const scale = 2; // High-res rendering
      const viewport = page.getViewport({ scale });
      const canvas = document.createElement("canvas");
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      const ctx = canvas.getContext("2d");
      await page.render({ canvasContext: ctx, viewport }).promise;
      const dataUrl = canvas.toDataURL("image/png");
      this.setState((prev) => ({
        pageImages: { ...prev.pageImages, [pageNum]: dataUrl },
        loadingPage: false,
      }));
    } catch (err) {
      console.error("Failed to render page:", err);
      this.setState({ loadingPage: false });
    }
  };

  processDataToText = (data, indentLevel = 0) => {
    if (!data) {
      return "No data available";
    }

    try {
      // If data is a string, try to parse it as JSON
      if (typeof data === "string") {
        try {
          const parsed = JSON.parse(data);
          return this.processDataToText(parsed, indentLevel);
        } catch (e) {
          // If it's not JSON, return as is
          return `${" ".repeat(indentLevel * 4)}${data}\n`;
        }
      }

      let output = "";
      const indent = " ".repeat(indentLevel * 4);

      if (Array.isArray(data)) {
        // For arrays in the data field, don't show indices
        if (indentLevel === 0 && data.length === 1) {
          return this.processDataToText(data[0], indentLevel);
        }
        // For other arrays (like division_requesting_credit_for), show items directly
        data.forEach((item) => {
          output += this.processDataToText(item, indentLevel);
        });
      } else if (typeof data === "object" && data !== null) {
        Object.entries(data).forEach(([key, value]) => {
          // Skip metadata fields that we don't want to display
          if (
            key === "status" ||
            key === "last_edited" ||
            key === "content_type"
          )
            return;

          // If the key is "content", return the content directly without any formatting or labels
          if (key === "content") {
            if (typeof value === "string") {
              output += value;
            } else {
              output += this.processDataToText(value, indentLevel);
            }
            return;
          }

          // Special handling for the "data" field at root level
          if (indentLevel === 0 && key === "data") {
            output += this.processDataToText(value, indentLevel);
            return;
          }

          // For all other fields, format them with labels
          const displayKey = key
            .split("_")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ");

          output += `${indent}${displayKey}: ${
            typeof value === "object" && value !== null ? "\n" : ""
          }`;
          if (typeof value === "object" && value !== null) {
            output += this.processDataToText(value, indentLevel + 1);
          } else {
            output += `${value}\n`;
          }
        });
      } else {
        output += `${indent}${String(data)}\n`;
      }

      return output;
    } catch (error) {
      console.error("Error in processDataToText:", error);
      return "Error processing data";
    }
  };

  handleBack = () => {
    this.props.navigate(
      `/dashboard/pdf-analyzer/result-summary/${this.fetchID()}/`
    );
  };

  handleEditClick = () => {
    const analysisOutput = this.props.pdfAnalysisOutput;
    console.log("1. Full pdfAnalysisOutput:", analysisOutput);

    if (!analysisOutput) {
      this.setState({ error: "No valid data structure found" });
      return;
    }

    // Log each level of the data structure
    console.log("2. analysis_output field:", analysisOutput.analysis_output);
    console.log("3. data field:", analysisOutput.analysis_output?.data);
    console.log("4. first item:", analysisOutput.analysis_output?.data?.[0]);

    // Try getting the data directly from pdfAnalysisOutput if it's already the inner structure
    const dataToEdit =
      analysisOutput.analysis_output?.data?.[0] ||
      analysisOutput.data?.[0] ||
      analysisOutput;

    console.log("5. Final dataToEdit:", dataToEdit);

    // Convert the data to markdown format instead of JSON
    let markdownContent = this.processDataToMarkdown(dataToEdit);
    console.log("6. Markdown content for editing:", markdownContent);

    this.setState({
      isEditing: true,
      editedContent: markdownContent,
    });
  };

  // New method to convert data to markdown format
  processDataToMarkdown = (data) => {
    if (!data) {
      return "No data available";
    }

    try {
      // If data is a string, check if it's already markdown
      if (typeof data === "string") {
        // If it's a JSON string with escaped characters, parse and process it
        if (data.startsWith('"') && data.endsWith('"')) {
          // Remove outer quotes and unescape the content
          const unescaped = data
            .slice(1, -1)
            .replace(/\\n/g, "\n")
            .replace(/\\"/g, '"')
            .replace(/\\\\/g, "\\");
          return unescaped;
        }
        // If it's already markdown or plain text, return as is
        return data;
      }

      // If it's an object, convert to markdown format
      let markdownOutput = "";

      if (Array.isArray(data)) {
        if (data.length === 1) {
          return this.processDataToMarkdown(data[0]);
        }
        data.forEach((item, index) => {
          markdownOutput += `## Item ${index + 1}\n\n`;
          markdownOutput += this.processDataToMarkdown(item);
          markdownOutput += "\n\n";
        });
      } else if (typeof data === "object" && data !== null) {
        Object.entries(data).forEach(([key, value]) => {
          // Skip metadata fields that we don't want to display
          if (
            key === "status" ||
            key === "last_edited" ||
            key === "content_type"
          )
            return;

          // If the key is "content", return the content directly without formatting
          if (key === "content") {
            markdownOutput += this.processDataToMarkdown(value);
            return;
          }

          // Format the key to be more readable
          const displayKey = key
            .split("_")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ");

          if (typeof value === "string") {
            // Check if the value is a JSON string with markdown content
            if (value.startsWith('"') && value.endsWith('"')) {
              const unescaped = value
                .slice(1, -1)
                .replace(/\\n/g, "\n")
                .replace(/\\"/g, '"')
                .replace(/\\\\/g, "\\");
              markdownOutput += `# ${displayKey}\n\n${unescaped}\n\n`;
            } else {
              markdownOutput += `**${displayKey}:** ${value}\n\n`;
            }
          } else if (typeof value === "object" && value !== null) {
            markdownOutput += `## ${displayKey}\n\n`;
            markdownOutput += this.processDataToMarkdown(value);
            markdownOutput += "\n\n";
          } else {
            markdownOutput += `**${displayKey}:** ${value}\n\n`;
          }
        });
      } else {
        markdownOutput += String(data);
      }

      return markdownOutput;
    } catch (error) {
      console.error("Error in processDataToMarkdown:", error);
      return "Error processing data";
    }
  };

  handleCancelEdit = () => {
    const analysisOutput = this.props.pdfAnalysisOutput?.analysis_output;
    this.setState({
      isEditing: false,
      editedContent: "",
      error: null,
    });
  };

  handleContentChange = (e) => {
    this.setState({ editedContent: e.target.value });
  };

  handleSaveChanges = async () => {
    const fileId = this.fetchID();
    this.setState({ isSaving: true, error: null });

    try {
      const { editedContent } = this.state;

      // Since we're now working with markdown content, we need to structure it properly
      // Instead of parsing as JSON, we'll save the markdown content directly
      const updatedData = {
        content: editedContent, // Store the markdown content
        content_type: "markdown",
        last_edited: new Date().toISOString(),
      };

      // Construct the complete structure
      const updatedOutput = {
        analysis_output: {
          data: [updatedData],
          status: "success",
        },
      };

      // Send the updated data to the API
      const response = await this.props.editAnalysisOutputAction(
        fileId,
        updatedOutput
      );

      console.log("API Response:", response);

      // After successful save, refresh the data
      await this.props.FetchIndividualFileAction(fileId);
      this.props.renderFileAction(fileId);

      this.setState({
        isEditing: false,
        isSaving: false,
        error: null,
      });
    } catch (error) {
      console.error("Save error:", error);
      this.setState({
        error: error.message || "Failed to save changes. Please try again.",
        isSaving: false,
      });
    }
  };

  // Function to detect if content is HTML
  isHtmlContent = (content) => {
    if (typeof content !== "string") return false;
    // Check for common HTML tags
    const htmlRegex = /<\/?[a-z][\s\S]*>/i;
    return htmlRegex.test(content);
  };

  // Function to detect if content is Markdown
  isMarkdownContent = (content) => {
    if (typeof content !== "string") return false;
    // Check for common markdown patterns
    const markdownPatterns = [
      /^#{1,6}\s+/m, // Headers
      /\*\*.*?\*\*/, // Bold text
      /\*.*?\*/, // Italic text
      /^-\s+/m, // List items
      /^\d+\.\s+/m, // Numbered lists
      /\[.*?\]\(.*?\)/, // Links
      /^>\s+/m, // Blockquotes
      /```[\s\S]*?```/, // Code blocks
      /`.*?`/, // Inline code
    ];

    return markdownPatterns.some((pattern) => pattern.test(content));
  };

  // Function to render content based on its type
  renderContent = (content) => {
    if (this.isHtmlContent(content)) {
      return (
        <div
          className="font-inter text-sm leading-relaxed text-gray-700 p-6"
          dangerouslySetInnerHTML={{ __html: content }}
        />
      );
    } else if (this.isMarkdownContent(content)) {
      return (
        <div className="font-inter text-sm leading-relaxed max-w-none text-gray-700 p-6">
          <ReactMarkdown
            components={{
              h1: ({ node, ...props }) => (
                <h1 className="text-xl font-bold mb-4 mt-2 text-gray-900 border-b border-blue-100 pb-2" {...props} />
              ),
              h2: ({ node, ...props }) => (
                <h2 className="text-lg font-semibold mb-3 mt-5 text-gray-800 border-b border-gray-100 pb-1.5" {...props} />
              ),
              h3: ({ node, ...props }) => (
                <h3 className="text-base font-semibold mb-2 mt-4 text-gray-700" {...props} />
              ),
              h4: ({ node, ...props }) => (
                <h4 className="text-sm font-semibold mb-1.5 mt-3 text-gray-600" {...props} />
              ),
              p: ({ node, ...props }) => (
                <p className="text-sm mb-3 text-gray-700 leading-relaxed" {...props} />
              ),
              strong: ({ node, ...props }) => (
                <strong className="font-semibold text-gray-900" {...props} />
              ),
              ul: ({ node, ...props }) => (
                <ul className="ml-4 mb-3 space-y-1.5" {...props} />
              ),
              ol: ({ node, ...props }) => (
                <ol className="list-decimal ml-5 mb-3 space-y-1.5 text-sm" {...props} />
              ),
              li: ({ node, ...props }) => (
                <li className="text-sm text-gray-700 leading-relaxed" {...props} />
              ),
              blockquote: ({ node, ...props }) => (
                <blockquote className="border-l-3 border-blue-200 pl-3 italic mb-3 text-gray-500 text-sm bg-blue-50/30 py-1.5 rounded-r" {...props} />
              ),
              code: ({ node, inline, ...props }) =>
                inline ? (
                  <code className="bg-blue-50 px-1.5 py-0.5 rounded text-xs font-mono text-blue-700" {...props} />
                ) : (
                  <code className="block bg-gray-50 border border-gray-100 p-3 rounded-lg text-xs font-mono overflow-x-auto text-gray-700" {...props} />
                ),
              pre: ({ node, ...props }) => (
                <pre className="bg-gray-50 border border-gray-100 p-3 rounded-lg overflow-x-auto mb-3 text-xs" {...props} />
              ),
              a: ({ node, ...props }) => (
                <a className="text-blue-600 underline hover:text-blue-800 hover:no-underline transition-colors" {...props} />
              ),
              hr: ({ node, ...props }) => (
                <hr className="my-5 border-gray-100" {...props} />
              ),
            }}
          >
            {content}
          </ReactMarkdown>
        </div>
      );
    } else {
      return (
        <pre className="font-inter text-sm leading-relaxed whitespace-pre-wrap text-gray-700 p-6">
          {content || "No data available"}
        </pre>
      );
    }
  };

  handleDownloadAsTXT = () => {
    const analysisOutput = this.props.pdfAnalysisOutput?.analysis_output || this.props.pdfAnalysisOutput;
    const content = this.processDataToText(analysisOutput);
    const blob = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "Extracted_Data.txt";
    a.click();
    URL.revokeObjectURL(url);
  };

  handleDownloadAsPDF = () => {
    const jsPDF = require("jspdf").default;
    const analysisOutput = this.props.pdfAnalysisOutput?.analysis_output || this.props.pdfAnalysisOutput;
    const content = this.processDataToText(analysisOutput);
    const doc = new jsPDF();
    const margin = 10;
    const pageHeight = doc.internal.pageSize.getHeight();
    const lineHeight = 8;
    const maxLineWidth = doc.internal.pageSize.getWidth() - margin * 2;
    const lines = doc.splitTextToSize(content, maxLineWidth);
    let cursorY = margin;
    lines.forEach((line) => {
      if (cursorY + lineHeight > pageHeight - margin) {
        doc.addPage();
        cursorY = margin;
      }
      doc.text(line, margin, cursorY);
      cursorY += lineHeight;
    });
    doc.save("Extracted_Data.pdf");
  };

  render() {
    const { pdfAnalysisOutput } = this.props;
    const { isEditing, editedContent, isSaving, error, currentPage, totalPages, pageImages, loadingPage } = this.state;
    const currentPageImage = pageImages[currentPage];

    const analysisOutput =
      pdfAnalysisOutput?.analysis_output || pdfAnalysisOutput;
    const displayContent = isEditing
      ? editedContent
      : this.processDataToText(analysisOutput);

    return (
      <div
        className="flex flex-col w-full min-h-screen"
        style={{
          background:
            "radial-gradient(ellipse at top left, #dbeafe 0%, transparent 50%), radial-gradient(ellipse at top right, #e0e7ff 0%, transparent 50%), radial-gradient(ellipse at bottom center, #eff6ff 0%, transparent 60%), #f8faff",
        }}
      >
        <div className="w-full flex flex-col px-4 md:px-6 pt-5 pb-10 gap-5">

          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={this.handleBack}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gradient-to-r from-gray-50 to-gray-100 border border-gray-200 text-sm font-medium text-gray-600 hover:from-gray-100 hover:to-gray-200 hover:text-gray-900 transition-all duration-150 font-inter"
              >
                <img src={ArrowLeftSVG} alt="back" className="w-4 h-4" />
                Back
              </button>
              <h1 className="font-inter font-bold text-xl text-gray-900">
                Document Comparison
              </h1>
            </div>
            <div className="flex items-center gap-2">
              {!isEditing && (
                <button
                  onClick={this.handleEditClick}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-blue-500 to-blue-600 text-white text-sm font-semibold hover:from-blue-600 hover:to-blue-700 transition-all duration-150 font-inter shadow-sm"
                >
                  <img src={PencilSimpleLineSVG} alt="Edit" className="w-4 h-4 brightness-0 invert" />
                  Edit Extracted Data
                </button>
              )}
            </div>
          </div>

          {/* Two-panel comparison */}
          <div className="flex gap-4 w-full" style={{ height: "calc(100vh - 200px)" }}>

            {/* Left: Original PDF */}
            <div className="flex-1 flex flex-col gap-2 min-w-0">
              <div className="flex items-center justify-between px-1">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-blue-400"></div>
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider font-inter">Original Document</span>
                </div>
                {totalPages > 0 && (
                  <div className="flex items-center gap-1.5">
                    <button
                      onClick={() => { if (currentPage > 1) this.renderPage(currentPage - 1); }}
                      disabled={currentPage <= 1}
                      className="w-7 h-7 rounded-md flex items-center justify-center border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-all text-xs font-inter"
                    >
                      &lt;
                    </button>
                    <span className="text-xs font-medium text-gray-500 font-inter min-w-[60px] text-center">
                      {currentPage} / {totalPages}
                    </span>
                    <button
                      onClick={() => { if (currentPage < totalPages) this.renderPage(currentPage + 1); }}
                      disabled={currentPage >= totalPages}
                      className="w-7 h-7 rounded-md flex items-center justify-center border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-all text-xs font-inter"
                    >
                      &gt;
                    </button>
                  </div>
                )}
              </div>
              <div
                className="flex-1 rounded-xl border border-blue-100 overflow-auto shadow-sm bg-white"
              >
                <div className="relative inline-block w-full">
                  {loadingPage ? (
                    <div className="w-full h-[60vw] md:h-[760px] bg-white rounded-lg flex items-center justify-center">
                      <div className="relative w-8 h-8">
                        <div className="absolute w-full h-full border-[3px] border-blue-100 rounded-full"></div>
                        <div className="absolute w-full h-full border-[3px] border-t-blue-500 border-r-transparent border-b-transparent border-l-transparent rounded-full animate-spin"></div>
                      </div>
                    </div>
                  ) : currentPageImage ? (
                    <img
                      src={currentPageImage}
                      alt={`Page ${currentPage}`}
                      className="block w-full h-auto"
                      draggable={false}
                    />
                  ) : (
                    <div className="w-full h-[60vw] md:h-[760px] bg-white flex items-center justify-center">
                      <div className="relative w-8 h-8">
                        <div className="absolute w-full h-full border-[3px] border-blue-100 rounded-full"></div>
                        <div className="absolute w-full h-full border-[3px] border-t-blue-500 border-r-transparent border-b-transparent border-l-transparent rounded-full animate-spin"></div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Right: Extracted Data */}
            <div className="flex-1 flex flex-col gap-2 min-w-0">
              <div className="flex items-center gap-2 px-1">
                <div className="w-2 h-2 rounded-full bg-green-400"></div>
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider font-inter">Extracted Data</span>
              </div>
              <div
                className="flex-1 rounded-xl border border-blue-100 shadow-sm flex flex-col overflow-hidden relative"
                style={{ background: "linear-gradient(135deg, #ffffff 0%, #f8faff 100%)" }}
              >
                {error && (
                  <div className="mx-4 mt-4 px-4 py-2.5 rounded-lg bg-red-50 border border-red-200 text-red-600 text-sm font-inter">
                    {error}
                  </div>
                )}
                {isEditing ? (
                  <div className="w-full flex-1 flex flex-col overflow-hidden" data-color-mode="light">
                    <MDEditor
                      value={editedContent}
                      onChange={(value) =>
                        this.setState({ editedContent: value || "" })
                      }
                      preview="edit"
                      hideToolbar={false}
                      height="100%"
                      style={{ flex: 1, overflow: "auto" }}
                      data-color-mode="light"
                    />
                  </div>
                ) : (
                  <div className="flex-1 overflow-auto pb-14">
                    {this.renderContent(displayContent)}
                  </div>
                )}

                {/* Action buttons — pinned to bottom of the card, overlaid so they don't affect height */}
                <div className="absolute bottom-0 left-0 right-0 flex justify-end gap-2 p-3 bg-gradient-to-t from-white via-white/95 to-transparent">
                  {isEditing ? (
                    <>
                      <button
                        onClick={this.handleCancelEdit}
                        disabled={isSaving}
                        className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-all font-inter shadow-sm"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={this.handleSaveChanges}
                        disabled={isSaving}
                        className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-green-500 to-green-600 text-white text-sm font-semibold hover:from-green-600 hover:to-green-700 transition-all font-inter shadow-sm disabled:opacity-50"
                      >
                        <img src={FloppyDiskBackSVG} alt="Save" className="w-4 h-4 brightness-0 invert" />
                        {isSaving ? "Saving..." : "Save Changes"}
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={this.handleDownloadAsTXT}
                        className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 hover:border-gray-300 transition-all font-inter shadow-sm"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                        Download TXT
                      </button>
                      <button
                        onClick={this.handleDownloadAsPDF}
                        className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-blue-500 to-blue-600 text-white text-sm font-semibold hover:from-blue-600 hover:to-blue-700 transition-all font-inter shadow-sm"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                        Download PDF
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => {
  console.log("mapStateToProps called with state:", {
    analysis: state.analysis,
    pdfAnalysisOutput: state.analysis?.pdfAnalysisOutput,
  });

  return {
    pdfAnalysisOutput: state.analysis?.pdfAnalysisOutput,
    renderedPDF: state.analysis?.renderedPDF,
    contentType: state.analysis?.contentType,
    PDFisRendered: state.analysis?.PDFisRendered,
    AddedFileID: state.analysis?.AddedFileID,
  };
};

const mapDispatchToProps = {
  renderFileAction,
  FetchIndividualFileAction,
  editAnalysisOutputAction,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouterHooks(PDFExtractorComparator));
